import { twMerge } from "tailwind-merge";

/**
 * Base skeleton element with pulse animation.
 * Use this for custom skeleton shapes not covered by other primitives.
 */
export function Skeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={twMerge(
        "bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer rounded",
        className
      )}
      style={style}
    />
  );
}

/**
 * Text line skeleton with standard sizes.
 */
export interface SkeletonTextProps {
  /** sm=h-4, md=h-5, lg=h-7, xl=h-8 */
  size?: "sm" | "md" | "lg" | "xl";
  /** Tailwind width class (e.g., "w-32", "w-full", "w-3/4") */
  width?: string;
  className?: string;
  style?: React.CSSProperties;
}

const textSizeClasses = {
  sm: "h-4",
  md: "h-5",
  lg: "h-7",
  xl: "h-8",
};

export function SkeletonText({
  size = "md",
  width = "w-full",
  className,
  style,
}: SkeletonTextProps) {
  return (
    <div
      className={twMerge(
        "bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer rounded",
        textSizeClasses[size],
        width,
        className
      )}
      style={style}
    />
  );
}

/**
 * Button skeleton with variant colors.
 */
export interface SkeletonButtonProps {
  /** primary=teal, secondary=gray, accent=purple, blue=blue */
  variant?: "primary" | "secondary" | "accent" | "blue";
  className?: string;
}

const buttonVariantClasses = {
  primary: "from-teal-100 via-teal-50 to-teal-100",
  secondary: "from-gray-100 via-gray-50 to-gray-100",
  accent: "from-purple-100 via-purple-50 to-purple-100",
  blue: "from-blue-100 via-blue-50 to-blue-100",
};

export function SkeletonButton({
  variant = "primary",
  className,
}: SkeletonButtonProps) {
  return (
    <div
      className={twMerge(
        "h-14 w-full rounded-full bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer",
        buttonVariantClasses[variant],
        className
      )}
    />
  );
}

/**
 * Avatar/circle skeleton with standard sizes.
 */
export interface SkeletonAvatarProps {
  /** sm=8 (32px), md=10 (40px), lg=16 (64px), xl=20 (80px) */
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

const avatarSizeClasses = {
  sm: "w-8 h-8",
  md: "w-10 h-10",
  lg: "w-16 h-16",
  xl: "w-20 h-20",
};

export function SkeletonAvatar({
  size = "md",
  className,
}: SkeletonAvatarProps) {
  return (
    <div
      className={twMerge(
        "bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-shimmer rounded-full",
        avatarSizeClasses[size],
        className
      )}
    />
  );
}

/**
 * Form input field skeleton.
 */
export interface SkeletonInputProps {
  className?: string;
}

export function SkeletonInput({ className }: SkeletonInputProps) {
  return (
    <div
      className={twMerge(
        "h-12 w-full bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 bg-[length:200%_100%] animate-shimmer rounded-lg border border-gray-200",
        className
      )}
    />
  );
}

/**
 * Form field with label skeleton.
 */
export interface SkeletonFieldProps {
  /** Width of the label (e.g., "w-24") */
  labelWidth?: string;
  className?: string;
}

export function SkeletonField({
  labelWidth = "w-24",
  className,
}: SkeletonFieldProps) {
  return (
    <div className={twMerge("space-y-2", className)}>
      <SkeletonText size="sm" width={labelWidth} />
      <SkeletonInput />
    </div>
  );
}
